import { useState } from 'react';
import { useEditIngredient, useMergeIngredient, useMergeSearch } from '../../api/review';
import type { MatchQueueRow, MergeCandidate } from '../../api/review';

/** The ingredient's other names, and what it is a kind of.
 *
 * Aliases are not cosmetic: `broadening_terms` searches every one of them at
 * every store, so adding "panko" to "panko breadcrumbs" is what makes a shelf
 * full of panko findable. Until now the only way to set one was a management
 * command, so the fix existed and could not be reached.
 *
 * `onSaved` fires once the names are stored, and is how the shops get searched
 * with them straight away rather than at the next nightly refresh.
 */
export default function IngredientEditor({
  row,
  onSaved,
}: {
  row: MatchQueueRow;
  onSaved?: () => void;
}) {
  const edit = useEditIngredient();
  const merge = useMergeIngredient();
  const [aliases, setAliases] = useState(row.aliases.join(', '));
  const [parent, setParent] = useState(row.parent);
  const [mergeTerm, setMergeTerm] = useState('');
  const [picked, setPicked] = useState<MergeCandidate | null>(null);
  const { data: candidates } = useMergeSearch(mergeTerm, row.id);

  const dirty = aliases !== row.aliases.join(', ') || parent !== row.parent;

  return (
    <div className="ing-editor">
      <label className="ing-editor__field">
        <span>Also known as</span>
        <input
          value={aliases}
          aria-label={`Other names for ${row.name}`}
          onChange={(event) => setAliases(event.target.value)}
        />
        <em className="muted small">
          Comma separated, used for matching ingredients to store inventory.
        </em>
      </label>

      <label className="ing-editor__field">
        <span>A kind of</span>
        <input
          value={parent}
          placeholder="e.g. breadcrumb"
          aria-label={`What ${row.name} is a kind of`}
          onChange={(event) => setParent(event.target.value)}
        />
        <em className="muted small">
          The broader category of item this belongs to. Used as a backup when the ingredient isn't available.
        </em>
      </label>

      <div className="ing-editor__actions">
        <button
          type="button"
          className={dirty ? 'btn btn--small btn--primary' : 'btn btn--small'}
          disabled={!dirty || edit.isPending}
          onClick={() =>
            edit.mutate(
              {
                id: row.id,
                aliases: aliases
                  .split(',')
                  .map((part) => part.trim())
                  .filter(Boolean),
                parent: parent.trim() || null,
              },
              { onSuccess: () => onSaved?.() },
            )
          }
        >
          {edit.isPending ? 'Saving…' : 'Save names'}
        </button>
        {edit.isError && (
          <span className="ing-editor__error">
            {(edit.error as Error).message}
          </span>
        )}
      </div>

      {/* Merging is destructive and never inferred: "butter lettuce" shares a
          word with "butter" and is not a kind of it, so both sides are named
          by a person and confirmed before anything moves. */}
      {/* Merging is destructive and never inferred: "butter lettuce" shares a
          word with "butter" and is not a kind of it, so the other side is
          searched for and named by a person, then confirmed. */}
      <details className="ing-editor__merge">
        <summary>This is the same food as another ingredient</summary>
        <p className="muted small">
          Its recipes and store products move here and its name is kept as an
          alias, then it is deleted. This cannot be undone.
        </p>

        <input
          type="search"
          value={mergeTerm}
          placeholder="search by name or alias — “panko”"
          aria-label={`Search for an ingredient to merge into ${row.name}`}
          onChange={(event) => {
            setMergeTerm(event.target.value);
            setPicked(null);
          }}
        />

        {candidates && candidates.length === 0 && mergeTerm.trim().length >= 2 && (
          <p className="muted small">No other ingredient matches that.</p>
        )}

        {!picked && candidates && candidates.length > 0 && (
          <ul className="merge-results">
            {candidates.map((option) => (
              <li key={option.id}>
                <button type="button" onClick={() => setPicked(option)}>
                  <span className="merge-results__name">{option.name}</span>
                  {/* The counts are what decide which way round to merge: the
                      row holding the products is usually the keeper. */}
                  <span className="muted small">
                    {option.recipes.toLocaleString()} recipe
                    {option.recipes === 1 ? '' : 's'} · {option.products} product
                    {option.products === 1 ? '' : 's'}
                    {option.aliases.length > 0 && ` · also “${option.aliases.join('”, “')}”`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {picked && (
          <div className="merge-confirm">
            <p>
              Fold <strong>{picked.name}</strong> ({picked.recipes.toLocaleString()}{' '}
              recipes, {picked.products} products) into <strong>{row.name}</strong>?
              {picked.recipes > row.uses && (
                <>
                  {' '}
                  <em>
                    Note {picked.name} is used by more recipes than {row.name} —
                    you may want to merge the other way round.
                  </em>
                </>
              )}
            </p>
            <div className="ing-editor__actions">
              <button
                type="button"
                className="btn btn--small danger"
                disabled={merge.isPending}
                onClick={() => {
                  merge.mutate({ id: row.id, loser: picked.id });
                  setPicked(null);
                  setMergeTerm('');
                }}
              >
                {merge.isPending ? 'Merging…' : `Yes, fold ${picked.name} in`}
              </button>
              <button
                type="button"
                className="btn btn--small"
                onClick={() => setPicked(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {merge.isError && (
          <span className="ing-editor__error">{(merge.error as Error).message}</span>
        )}
      </details>
    </div>
  );
}
